import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { createCompetitor, deleteCompetitor, listCompetitors, updateCompetitor } from "./api";
import { ConfirmModal, errorMessage, formatTimestamp } from "./shared";

const EMPTY_FORM = {
  name: "",
  domains: "",
  aliases: "",
  parentNames: "",
  subsidiaryNames: "",
  blockRelatedOrganizations: false,
  notes: "",
};

const LIST_FIELDS = ["domains", "aliases", "parentNames", "subsidiaryNames"];

function splitList(value) {
  return String(value || "")
    .split(/[,\n]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function toForm(competitor) {
  if (!competitor) {
    return EMPTY_FORM;
  }

  const form = {
    ...EMPTY_FORM,
    name: competitor.name || "",
    blockRelatedOrganizations: Boolean(competitor.blockRelatedOrganizations),
    notes: competitor.notes || "",
  };
  LIST_FIELDS.forEach((key) => {
    form[key] = (competitor[key] || []).join(", ");
  });

  return form;
}

function toPayload(form) {
  const payload = {
    name: String(form.name || "").trim(),
    blockRelatedOrganizations: Boolean(form.blockRelatedOrganizations),
    notes: String(form.notes || "").trim(),
  };
  LIST_FIELDS.forEach((key) => {
    payload[key] = splitList(form[key]);
  });

  return payload;
}

function CompetitorForm({ env, competitor, onSaved, onCancel }) {
  const [form, setForm] = useState(() => toForm(competitor));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
    setError(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = toPayload(form);
      const response = competitor ? await updateCompetitor(env, competitor.id, payload) : await createCompetitor(env, payload);
      toast.success(competitor ? "Competitor updated" : "Competitor added");
      onSaved(response.competitor);
      setForm(EMPTY_FORM);
    } catch (submitError) {
      setError(errorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="ref-add-panel">
      <h2 className="ref-section-title">{competitor ? `Edit ${competitor.name}` : "Add competitor"}</h2>
      <p className="ref-section-subtitle">
        Exact domain matches can block access. Name and alias matches only send an applicant to review.
      </p>
      <form className="di-form" onSubmit={submit}>
        <label className="di-field">
          Company name (required)
          <input className="ref-input" required maxLength={120} value={form.name} onChange={update("name")} />
        </label>
        <label className="di-field">
          Known domains
          <input className="ref-input" placeholder="example.com, example-lending.com" value={form.domains} onChange={update("domains")} />
        </label>
        <label className="di-field">
          Alternative names
          <input className="ref-input" placeholder="Comma separated" value={form.aliases} onChange={update("aliases")} />
        </label>
        <label className="di-field">
          Parent organizations
          <input className="ref-input" placeholder="Comma separated" value={form.parentNames} onChange={update("parentNames")} />
        </label>
        <label className="di-field">
          Subsidiaries
          <input className="ref-input" placeholder="Comma separated" value={form.subsidiaryNames} onChange={update("subsidiaryNames")} />
        </label>
        <label className="di-field di-field-wide">
          Internal notes
          <input className="ref-input" maxLength={500} value={form.notes} onChange={update("notes")} />
        </label>
        <label className="di-checkbox">
          <input type="checkbox" checked={form.blockRelatedOrganizations} onChange={update("blockRelatedOrganizations")} />
          Block confirmed parent and subsidiary organizations (otherwise they go to review)
        </label>
        <div className="di-form-actions">
          {competitor ? (
            <button type="button" className="ref-btn ref-btn-ghost" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </button>
          ) : null}
          <button type="submit" className="ref-btn ref-btn-primary" disabled={isSubmitting || !form.name.trim()}>
            {isSubmitting ? "Saving…" : competitor ? "Save changes" : "Add competitor"}
          </button>
        </div>
      </form>
      {error ? <div className="ref-inline-error" role="alert">{error}</div> : null}
    </section>
  );
}

export function CompetitorsPanel({ env }) {
  const [competitors, setCompetitors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listCompetitors(env);
      setCompetitors(response.competitors || []);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setIsLoading(false);
    }
  }, [env]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = async (competitor) => {
    const status = competitor.status === "active" ? "inactive" : "active";
    try {
      await updateCompetitor(env, competitor.id, { status });
      toast.success(status === "active" ? "Competitor activated" : "Competitor deactivated");
      load();
    } catch (toggleError) {
      toast.error(errorMessage(toggleError));
    }
  };

  const confirmDelete = async () => {
    setIsBusy(true);
    try {
      await deleteCompetitor(env, pendingDelete.id);
      toast.success("Competitor deleted");
      setPendingDelete(null);
      load();
    } catch (deleteError) {
      toast.error(errorMessage(deleteError));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <>
      <CompetitorForm
        key={editing?.id || "new"}
        env={env}
        competitor={editing}
        onSaved={() => {
          setEditing(null);
          load();
        }}
        onCancel={() => setEditing(null)}
      />

      {error ? <div className="ref-error" role="alert">{error}</div> : null}

      {isLoading ? (
        <div className="ref-empty">Loading competitors…</div>
      ) : (
        <div className="ref-table-wrapper">
          {competitors.length === 0 ? (
            <div className="ref-empty">No competitors yet. Add one above.</div>
          ) : (
            <table className="ref-table">
              <thead>
                <tr>
                  <th>Competitor</th>
                  <th>Domains</th>
                  <th>Relationships</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th className="ref-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((competitor) => (
                  <tr key={competitor.id}>
                    <td>
                      <div className="ref-org-name">{competitor.name}</div>
                      {competitor.aliases?.length ? <div className="ref-muted di-note">Also: {competitor.aliases.join(", ")}</div> : null}
                      {competitor.notes ? <div className="ref-muted di-note">{competitor.notes}</div> : null}
                    </td>
                    <td>{competitor.domains?.join(", ") || "—"}</td>
                    <td>
                      {competitor.parentNames?.length ? <div className="di-note">Parents: {competitor.parentNames.join(", ")}</div> : null}
                      {competitor.subsidiaryNames?.length ? <div className="di-note">Subsidiaries: {competitor.subsidiaryNames.join(", ")}</div> : null}
                      <div className="ref-muted di-note">{competitor.blockRelatedOrganizations ? "Related organizations blocked" : "Related organizations reviewed"}</div>
                    </td>
                    <td>
                      <span className={`ref-badge da-competitor-${competitor.status}`}>{competitor.status === "active" ? "Active" : "Inactive"}</span>
                    </td>
                    <td>{formatTimestamp(competitor.updatedAt)}</td>
                    <td className="ref-actions-col">
                      <div className="ref-row-actions di-row-actions">
                        <button type="button" className="ref-btn ref-btn-ghost ref-btn-sm" onClick={() => setEditing(competitor)}>
                          Edit
                        </button>
                        <button type="button" className="ref-btn ref-btn-ghost ref-btn-sm" onClick={() => toggleStatus(competitor)}>
                          {competitor.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                        <button type="button" className="ref-btn ref-btn-danger ref-btn-sm" onClick={() => setPendingDelete(competitor)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <ConfirmModal
        action={
          pendingDelete
            ? {
                title: `Delete ${pendingDelete.name}?`,
                description: "Applicants are no longer screened against this entry. Past decisions keep their evidence. Deactivate instead to keep the record.",
                confirmLabel: "Delete competitor",
                tone: "danger",
              }
            : null
        }
        isBusy={isBusy}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
